import { useRecoilValue } from 'recoil';
import { useHippoText } from '../../../hooks/useHippoText';
import WizardPagetemplate from '../../../templates/WizardPagetemplate';
import CoverableDetailsTemplate from '../../../templates/CoverableDetailsTemplate';
import { SelectField } from '@flos/react-ui';
import { useState } from 'react';
import CallMeModal from '../../../shared_components/CallMeModal/CallMeModal';
import { useUpdateCoverableInformation } from '../../../recoil/hooks/useUpdateCoverableInformation';
import { getCoverableIndex } from '../../../utils/coverablesUtil';
import { getOnsaCurrentProductSelector } from '../../../recoil/selectors/getOnsaCurrentProductSelector';
import { allCoverablesOnsaAtom } from '../../../recoil/atoms/allCoverablesOnsaAtom';
import { useSaveClaimsHistoryInformation } from '../../../hooks/useSaveClaimsHistoryInformation';
import type { ClaimsHistoryInformationToUpdateDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa';
import { track } from '../../../utils/trackingUtil';

const CarNumberOfClaimsStep = ({
    goNext,
    goPrevious
}: {
    goNext: () => void;
    goPrevious: () => void;
}): JSX.Element => {
    const getHippoText = useHippoText();
    const { saveClaimsHistoryInformation } = useSaveClaimsHistoryInformation();
    const { updateCoverableInformation } = useUpdateCoverableInformation();
    const coverables = useRecoilValue(allCoverablesOnsaAtom);
    const currentProduct = useRecoilValue(getOnsaCurrentProductSelector);
    const { cars = [] } = coverables;
    const coverableIndex = getCoverableIndex(cars, currentProduct.fixedId);
    const coverableData = cars[coverableIndex];
    const [numberOfClaimsInput, setNumberOfClaimsInput] = useState(
        coverableData?.numberOfClaims ?? ''
    );
    const [claimYear, setClaimYear] = useState(coverableData?.latestClaimYear ?? '');
    const [showModal, setShowModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const getPastThreeYearsList = (): string[] => {
        const currentYear: number = new Date().getFullYear();
        return [currentYear.toString(), (currentYear - 1).toString(), (currentYear - 2).toString()];
    };

    const claimIsNotSetOrMoreThanThree = !numberOfClaimsInput || Number(numberOfClaimsInput) === 4;
    const claimIsSetWithoutClaimYear =
        Number(numberOfClaimsInput) > 0 && Number(numberOfClaimsInput) <= 3 && !claimYear;

    const goToNextPage = (): void => {
        setIsLoading(true);
        const numberOfClaims = Number(numberOfClaimsInput);
        const latestClaimYear = Number(numberOfClaims) === 0 ? undefined : Number(claimYear);
        updateCoverableInformation({
            numberOfClaims,
            latestClaimYear
        });
        const updatedCoverable: ClaimsHistoryInformationToUpdateDTO = {
            productToUpdate: 'Bilforsikring',
            car: {
                fixedId: coverableData.fixedId,
                numberOfClaims,
                lastClaimYear: latestClaimYear ?? 0
            }
        };

        void saveClaimsHistoryInformation(updatedCoverable).then(() => {
            track(`${currentProduct.flavourName}ContentCoverableInformationNumberOfClaims`, [
                { numberOfClaims },
                {
                    lastClaimYear: latestClaimYear ?? 0
                }
            ]);
            setIsLoading(false);
            goNext();
        });
    };

    return (
        <WizardPagetemplate
            onPrevious={goPrevious}
            onNext={goToNextPage}
            disableNext={claimIsNotSetOrMoreThanThree || claimIsSetWithoutClaimYear || isLoading}
            disablePrevious={isLoading}
            isNextLoading={isLoading}
        >
            <CoverableDetailsTemplate
                header={getHippoText('DSS-COVERABLE-INFORMATION-STEP-NUMBER-OF-CLAIMS-HEADER')}
                subheader={getHippoText('DSS-COVERABLE-INFORMATION-STEP-NUMBER-OF-CLAIMS-TEXT')}
            >
                <p>{getHippoText('DSS-COVERABLE-INFORMATION-STEP-NUMBER-OF-CLAIMS-TEXT-TWO')}</p>
                <SelectField
                    label={getHippoText('DSS-COVERABLE-INFORMATION-STEP-NUMBER-OF-CLAIMS-FIELD')}
                    options={[
                        { label: '0', value: '0' },
                        { label: '1', value: '1' },
                        { label: '2', value: '2' },
                        { label: '3', value: '3' },
                        { label: 'Mere end 3', value: '4' }
                    ]}
                    onChange={(event) => {
                        const selectedValue = event.target.value;
                        if (selectedValue === '4') {
                            setShowModal(true);
                        }
                        setNumberOfClaimsInput(event.target.value);
                    }}
                    value={numberOfClaimsInput}
                    disabled={isLoading}
                />
                {Number(numberOfClaimsInput) > 0 ? (
                    <div>
                        <p>
                            {getHippoText(
                                'DSS-COVERABLE-INFORMATION-STEP-NUMBER-OF-CLAIMS-DATE-HEADER'
                            )}
                        </p>
                        <SelectField
                            label={getHippoText(
                                'DSS-COVERABLE-INFORMATION-STEP-NUMBER-OF-CLAIMS-DATE-FIELD'
                            )}
                            options={getPastThreeYearsList()}
                            onChange={(event) => {
                                setClaimYear(event.target.value);
                            }}
                            value={claimYear}
                            disabled={isLoading}
                        />
                    </div>
                ) : (
                    <></>
                )}
                {Number(numberOfClaimsInput) === 4 && showModal ? (
                    <CallMeModal
                        activityCode="dss_call_request"
                        title={getHippoText('DSS-CALL-ME-MODAL-MVP-BVR-83-HEADER')}
                        description={getHippoText('DSS-CALL-ME-MODAL-MVP-BVR-83-SUBHEADER')}
                        subdescription={getHippoText('DSS-CALL-ME-MODAL-MVP-BVR-83-TEXT')}
                        showModal={showModal}
                        closeModal={() => {
                            setShowModal(false);
                        }}
                    />
                ) : (
                    <></>
                )}
            </CoverableDetailsTemplate>
        </WizardPagetemplate>
    );
};
export default CarNumberOfClaimsStep;
